import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import type { KeycloakConfig } from '../config/types';

export interface UseTokenLifecycleOptions {
  /**
   * Keycloak configuration for token lifecycle settings
   */
  config: KeycloakConfig;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;

  /**
   * Callback when token refresh starts
   */
  onRefreshStart?: () => void;

  /**
   * Callback when token refresh succeeds
   */
  onRefreshSuccess?: () => void;

  /**
   * Callback when token refresh fails
   */
  onRefreshError?: (error: Error) => void;

  /**
   * Callback when refresh token lifetime expires
   */
  onRefreshTokenExpired?: () => void;
}

export interface UseTokenLifecycleReturn {
  /**
   * Whether token is being refreshed
   */
  isRefreshing: boolean;

  /**
   * Whether refresh token has exceeded max lifetime
   */
  isRefreshTokenExpired: boolean;

  /**
   * Manually trigger token refresh
   */
  refreshToken: () => Promise<void>;

  /**
   * Check if tokens need refresh and do it if needed
   */
  checkAndRefreshTokens: () => Promise<boolean>;
}

const STORAGE_KEY_PREFIX = 'keycloak_first_login_';

type DeferredReason = 'offline' | 'network';

const isNavigatorOffline = (): boolean =>
  typeof navigator !== 'undefined' &&
  'onLine' in navigator &&
  navigator.onLine === false;

const isTransientNetworkError = (error: Error): boolean => {
  const message = error.message?.toLowerCase?.() ?? '';
  if (!message) {
    return false;
  }

  return (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('fetch') ||
    message.includes('timeout') ||
    message.includes('temporarily unavailable') ||
    message.includes('dns') ||
    message.includes('offline')
  );
};

/**
 * Hook to manage token lifecycle including startup refresh and max lifetime tracking
 *
 * This hook provides proactive token management:
 * - Checks tokens on app startup and refreshes if needed
 * - Tracks refresh token max lifetime
 * - Provides manual refresh capabilities
 *
 * @param options - Configuration options
 * @returns Token lifecycle methods and state
 *
 * @example
 * ```tsx
 * import { useTokenLifecycle } from 'antaeus.keycloak.react';
 *
 * function App() {
 *   const { isRefreshing, isRefreshTokenExpired } = useTokenLifecycle({
 *     config: myConfig,
 *     onRefreshStart: () => console.log('Refreshing...'),
 *     onRefreshSuccess: () => console.log('Refreshed!'),
 *     onRefreshError: (error) => console.error('Failed:', error),
 *   });
 *
 *   if (isRefreshing) return <div>Loading...</div>;
 *   if (isRefreshTokenExpired) return <div>Please sign in again</div>;
 *
 *   return <YourApp />;
 * }
 * ```
 */
export function useTokenLifecycle(
  options: UseTokenLifecycleOptions
): UseTokenLifecycleReturn {
  const {
    config,
    debug = false,
    onRefreshStart,
    onRefreshSuccess,
    onRefreshError,
    onRefreshTokenExpired,
  } = options;

  const auth = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshTokenExpired, setIsRefreshTokenExpired] = useState(false);
  const hasCheckedOnStartup = useRef(false);
  const supportsRefreshTokens = config.silentRenew?.useRefreshToken !== false;
  const [pendingRetry, setPendingRetry] = useState<DeferredReason | null>(null);
  const deferredRefreshReasonRef = useRef<DeferredReason | null>(null);

  const enqueueRetry = useCallback((reason: DeferredReason) => {
    setPendingRetry((current) => (current === reason ? current : reason));
  }, []);

  const log = useCallback(
    (message: string, ...args: any[]) => {
      if (debug) {
        console.log('[TokenLifecycle]', message, ...args);
      }
    },
    [debug]
  );

  /**
   * Get the storage key for tracking first login time
   */
  const getStorageKey = useCallback((): string => {
    const authority = config.authority;
    const clientId = config.clientId;
    return `${STORAGE_KEY_PREFIX}${authority}_${clientId}`;
  }, [config.authority, config.clientId]);

  /**
   * Store the first login timestamp
   */
  const storeFirstLoginTime = useCallback(() => {
    const key = getStorageKey();
    const existingTime = localStorage.getItem(key);

    if (!existingTime) {
      const now = Date.now();
      localStorage.setItem(key, now.toString());
      log('Stored first login time:', new Date(now).toISOString());
    }
  }, [getStorageKey, log]);

  /**
   * Check if refresh token has exceeded max lifetime
   */
  const checkRefreshTokenLifetime = useCallback((): boolean => {
    const maxLifetime = config.silentRenew?.maxRefreshTokenLifetime;

    // If no max lifetime configured, never expire
    if (!maxLifetime || maxLifetime <= 0) {
      return false;
    }

    const key = getStorageKey();
    const firstLoginStr = localStorage.getItem(key);

    if (!firstLoginStr) {
      // No first login recorded, store current time
      storeFirstLoginTime();
      return false;
    }

    const firstLoginTime = parseInt(firstLoginStr, 10);
    const now = Date.now();
    const elapsedSeconds = (now - firstLoginTime) / 1000;

    log('Refresh token lifetime check:', {
      firstLogin: new Date(firstLoginTime).toISOString(),
      elapsedSeconds,
      maxLifetime,
      isExpired: elapsedSeconds >= maxLifetime,
    });

    return elapsedSeconds >= maxLifetime;
  }, [config.silentRenew?.maxRefreshTokenLifetime, getStorageKey, log, storeFirstLoginTime]);

  /**
   * Manually refresh tokens
   */
  const refreshToken = useCallback(async (): Promise<void> => {
    // Atomic check-and-set to prevent race condition
    if (isRefreshing) {
      log('Refresh already in progress, skipping');
      return;
    }

    // Set refreshing flag immediately (before any async operations)
    setIsRefreshing(true);
    deferredRefreshReasonRef.current = null;

    try {
      // Check if refresh token has exceeded max lifetime
      if (checkRefreshTokenLifetime()) {
        log('Refresh token exceeded max lifetime, cannot refresh');
        setIsRefreshTokenExpired(true);
        onRefreshTokenExpired?.();
        return;
      }

      if (isNavigatorOffline()) {
        log('Device offline, deferring token refresh until connection is restored');
        deferredRefreshReasonRef.current = 'offline';
        enqueueRetry('offline');
        return;
      }

      onRefreshStart?.();
      log('Starting token refresh');

      await auth.signinSilent();

      log('Token refresh successful');
      onRefreshSuccess?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      if (isTransientNetworkError(err)) {
        const reason: DeferredReason = isNavigatorOffline() ? 'offline' : 'network';
        deferredRefreshReasonRef.current = reason;
        if (reason === 'offline') {
          log('Token refresh deferred because device is offline');
        } else {
          log(
            'Token refresh failed due to transient network issue, will retry automatically:',
            err.message
          );
        }
        enqueueRetry(reason);
        onRefreshError?.(err);
        return;
      }

      log('Token refresh failed:', err.message);
      onRefreshError?.(err);
      throw err;
    } finally {
      setIsRefreshing(false);
    }
  }, [
    isRefreshing,
    auth,
    checkRefreshTokenLifetime,
    onRefreshStart,
    onRefreshSuccess,
    onRefreshError,
    onRefreshTokenExpired,
    enqueueRetry,
    log,
  ]);

  /**
   * Check if tokens are expired or about to expire and refresh if needed
   */
  const checkAndRefreshTokens = useCallback(async (): Promise<boolean> => {
    if (!auth.user) {
      log('No user session, skipping token check');
      return false;
    }

    // Check refresh token max lifetime
    if (checkRefreshTokenLifetime()) {
      log('Refresh token exceeded max lifetime');
      setIsRefreshTokenExpired(true);
      onRefreshTokenExpired?.();

      // Clear the first login timestamp and sign out
      const key = getStorageKey();
      localStorage.removeItem(key);

      try {
        await auth.removeUser();
      } catch (error) {
        log('Error removing user after max lifetime:', error);
        // Continue even if removeUser fails
      }

      return false;
    }

    const expiresAt = auth.user.expires_at;
    if (!expiresAt) {
      log('No expiration time found, skipping token check');
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    const threshold = config.silentRenew?.thresholdSeconds || 300;
    const timeUntilExpiry = expiresAt - now;

    log('Token expiration check:', {
      expiresAt: new Date(expiresAt * 1000).toISOString(),
      now: new Date(now * 1000).toISOString(),
      timeUntilExpiry,
      threshold,
      needsRefresh: timeUntilExpiry <= threshold,
    });

    // If token is expired or about to expire, refresh it
    if (timeUntilExpiry <= threshold) {
      try {
        await refreshToken();

        if (deferredRefreshReasonRef.current) {
          log(
            'Token refresh was deferred due to pending retry:',
            deferredRefreshReasonRef.current
          );
          deferredRefreshReasonRef.current = null;
          return false;
        }

        return true;
      } catch (error) {
        log('Failed to refresh tokens:', error);
        return false;
      }
    }

    log('Tokens are still valid, no refresh needed');
    return false;
  }, [
    auth,
    config.silentRenew?.thresholdSeconds,
    checkRefreshTokenLifetime,
    refreshToken,
    onRefreshTokenExpired,
    getStorageKey,
    log,
  ]);

  /**
   * Effect: Retry deferred refresh attempts when connectivity improves
   */
  useEffect(() => {
    if (pendingRetry === null) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    let cancelled = false;

    const executeRetry = () => {
      if (cancelled) {
        return;
      }

      if (isRefreshing) {
        log('Skipping scheduled token refresh retry because a refresh is already in progress');
        return;
      }

      refreshToken().catch((err) => {
        log('Scheduled token refresh retry failed:', err);
      });
    };

    if (pendingRetry === 'offline') {
      log('Waiting for online event to retry token refresh');
      const handleOnline = () => {
        if (cancelled) {
          return;
        }
        log('Online event detected, retrying deferred token refresh');
        setPendingRetry(null);
        executeRetry();
      };

      window.addEventListener('online', handleOnline, { once: true });

      return () => {
        cancelled = true;
        window.removeEventListener('online', handleOnline);
      };
    }

    const timeoutId = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      log('Retrying token refresh after transient network failure delay');
      setPendingRetry(null);
      executeRetry();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [pendingRetry, refreshToken, isRefreshing, log]);

  /**
   * Effect: Check and refresh tokens on startup if strategy includes 'startup'
   */
  useEffect(() => {
    const strategy = config.silentRenew?.refreshStrategy || 'both';
    const shouldCheckOnStartup = strategy === 'startup' || strategy === 'both';
    const hasRefreshToken = Boolean(auth.user?.refresh_token);

    if (!shouldCheckOnStartup || hasCheckedOnStartup.current) {
      return;
    }

    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated) {
      if (!supportsRefreshTokens) {
        return;
      }

      if (!auth.user || !hasRefreshToken) {
        return;
      }
    }

    hasCheckedOnStartup.current = true;
    log(
      'Running startup token check with strategy:',
      strategy,
      'using refresh token:',
      !auth.isAuthenticated && supportsRefreshTokens
    );

    checkAndRefreshTokens().catch((error) => {
      log('Startup token check failed:', error);
    });
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    auth.user,
    config.silentRenew?.refreshStrategy,
    supportsRefreshTokens,
    checkAndRefreshTokens,
    log,
  ]);

  /**
   * Effect: prevent re-running startup logic until prerequisites change
   */
  useEffect(() => {
    if (hasCheckedOnStartup.current) {
      const hasUser = Boolean(auth.user);
      const hasRefreshToken = Boolean(auth.user?.refresh_token);
      // Only reset when user is logged out and we can't use refresh token
      // Don't reset during loading state (e.g., during silent renewal)
      if (!auth.isAuthenticated && (!supportsRefreshTokens || !hasUser || !hasRefreshToken)) {
        hasCheckedOnStartup.current = false;
      }
    }
  }, [auth.isAuthenticated, auth.user, supportsRefreshTokens]);

  /**
   * Effect: Store first login time when user logs in
   */
  useEffect(() => {
    if (auth.user && !auth.isLoading) {
      storeFirstLoginTime();
    }
  }, [auth.user, auth.isLoading, storeFirstLoginTime]);

  /**
   * Effect: Check refresh token lifetime periodically
   */
  useEffect(() => {
    if (!auth.isAuthenticated || auth.isLoading) {
      return;
    }

    const maxLifetime = config.silentRenew?.maxRefreshTokenLifetime;
    if (!maxLifetime || maxLifetime <= 0) {
      return;
    }

    // Check every 5 minutes
    const intervalId = setInterval(() => {
      if (checkRefreshTokenLifetime()) {
        log('Refresh token expired during periodic check');
        setIsRefreshTokenExpired(true);
        onRefreshTokenExpired?.();

        // Clear the first login timestamp and sign out
        const key = getStorageKey();
        localStorage.removeItem(key);

        auth.removeUser().catch((error) => {
          log('Error removing user during periodic check:', error);
          // Continue even if removeUser fails
        });
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [
    auth,
    config.silentRenew?.maxRefreshTokenLifetime,
    checkRefreshTokenLifetime,
    onRefreshTokenExpired,
    getStorageKey,
    log,
  ]);

  return {
    isRefreshing,
    isRefreshTokenExpired,
    refreshToken,
    checkAndRefreshTokens,
  };
}
